// ─── aiExplain.js — клиентский слой для AI-объяснений слов ─────────────────
// Решает три задачи:
//   1) Если пользователь вписал свой OpenRouter ключ в настройках — идёт
//      напрямую к OpenRouter (не тратит общий лимит /api/explain).
//   2) Иначе — стримит через нашу Vercel Edge Function /api/explain.
//   3) Кэширует ответы в localStorage (одно и то же слово в том же контексте
//      не должно дёргать сеть дважды).
//
// Почему OpenRouter, а не Gemini напрямую: OpenRouter принимает регистрацию
// из любой страны и проксирует к Gemini/Llama/Claude/etc, тогда как
// Gemini напрямую блочит большинство РФ-аккаунтов.

import { markSetting } from './settings';
import { isCustomAIReady, streamChat } from './customAI';

const BYOK_KEY        = "ac_openrouter_byok";
const CACHE_KEY       = "ac_ai_explain_cache";
const CACHE_MAX_ITEMS = 500;
const DEFAULT_MODEL   = "meta-llama/llama-3.3-70b-instruct:free";

export const getByokKey = () => {
  try { return (localStorage.getItem(BYOK_KEY) || "").trim(); } catch { return ""; }
};
export const setByokKey = (k) => {
  try { if (k) localStorage.setItem(BYOK_KEY, k.trim()); else localStorage.removeItem(BYOK_KEY); } catch {}
  markSetting(BYOK_KEY);
};

// ── Кэш ─────────────────────────────────────────────────────────────────────
// Ключ = слово + язык + хэш контекста. Контекст хэшируется чтобы маленькие
// правки текста не сбрасывали кэш на каждое слово. Простой 32-битный хэш
// достаточен — коллизии тут безвредны.
const _hash = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) { h = ((h << 5) - h + s.charCodeAt(i)) | 0; }
  return (h >>> 0).toString(36);
};
const _cacheKey = ({ word, srcLang, nativeLang, context }) =>
  `${(word||"").toLowerCase()}|${srcLang||""}|${nativeLang||""}|${_hash((context||"").slice(0,200))}`;

const _readCache = () => {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY) || "{}"); } catch { return {}; }
};
const _writeCache = (m) => {
  try {
    // LRU prune: если перевалили за CACHE_MAX_ITEMS — выкидываем самые старые.
    const entries = Object.entries(m);
    if (entries.length > CACHE_MAX_ITEMS) {
      entries.sort((a, b) => (a[1].ts || 0) - (b[1].ts || 0));
      const trimmed = Object.fromEntries(entries.slice(-CACHE_MAX_ITEMS));
      localStorage.setItem(CACHE_KEY, JSON.stringify(trimmed));
    } else {
      localStorage.setItem(CACHE_KEY, JSON.stringify(m));
    }
  } catch {}
};
export const getCachedExplain = (params) => {
  const m = _readCache();
  const hit = m[_cacheKey(params)];
  return hit?.text || null;
};
const _saveCache = (params, text) => {
  const m = _readCache();
  m[_cacheKey(params)] = { text, ts: Date.now() };
  _writeCache(m);
};

// ── Промпт (дубль серверного — для BYOK-режима без прокси) ─────────────────
const _buildPrompt = ({ word, srcLang, nativeLang, context }) => {
  const native = nativeLang || "Russian";
  const src = srcLang || "the source language";
  const ctx = (context || "").slice(0, 600);
  return `You are a concise, friendly language teacher. The student is learning ${src} and speaks ${native}.

The student tapped this word/phrase in their reading: "${word}"
${ctx ? `Sentence context: "${ctx}"` : ""}

Explain it in ${native}, in 120–180 words. Cover, in order:
1. Meaning in this specific context (one short sentence)
2. Grammar form if it's an inflection (case, tense, mood, etc.) — only if relevant
3. The base/dictionary form if different from the surface form
4. One natural usage example with translation
5. A quick memory hook or related word — only if it helps

Be plain-prose, no headings, no markdown bullets, no numbered list. Write as if speaking to the student.`;
};

// ── Стриминг от OpenRouter напрямую (BYOK) ──────────────────────────────────
async function* _streamOpenRouterDirect({ key, prompt, signal }) {
  const resp = await fetch("https://openrouter.ai/api/v1/chat/completions", {
    method: "POST",
    signal,
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${key}`,
      "HTTP-Referer": typeof window !== "undefined" ? window.location.origin : "https://audiocards.vercel.app",
      "X-Title": "AntonCards",
    },
    body: JSON.stringify({
      model: DEFAULT_MODEL,
      stream: true,
      temperature: 0.4,
      max_tokens: 500,
      messages: [{ role: "user", content: prompt }],
    }),
  });
  if (!resp.ok || !resp.body) {
    const t = await resp.text().catch(()=>"");
    throw new Error(`OpenRouter ${resp.status}: ${t.slice(0,200)}`);
  }
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const events = buf.split("\n\n");
    buf = events.pop() || "";
    for (const ev of events) {
      const line = ev.split("\n").find(l => l.startsWith("data: "));
      if (!line) continue;
      const payload = line.slice(6).trim();
      if (!payload || payload === "[DONE]") continue;
      try {
        const json = JSON.parse(payload);
        const delta = json?.choices?.[0]?.delta?.content;
        if (delta) yield delta;
      } catch {}
    }
  }
}

// ── Стриминг через наш /api/explain ─────────────────────────────────────────
async function* _streamProxy({ params, signal }) {
  // В приложении (Android, локальная разработка) своего /api нет — обращаемся к сайту.
  const base = window.location.hostname === "audiocards.vercel.app" ? "" : "https://audiocards.vercel.app";
  const resp = await fetch(`${base}/api/explain`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    signal,
    body: JSON.stringify(params),
  });
  if (!resp.ok || !resp.body) {
    const t = await resp.text().catch(()=>"");
    throw new Error(`Proxy ${resp.status}: ${t.slice(0,200)}`);
  }
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    const chunk = decoder.decode(value, { stream: true });
    if (chunk) yield chunk;
  }
}

// ── Публичный API ───────────────────────────────────────────────────────────
// streamExplanation(params, onChunk) — стримит ответ кусочками в onChunk;
// возвращает финальный полный текст. Кидает Error при сетевой ошибке.
// Использует кэш, если ответ уже есть — onChunk вызывается один раз с целым.
export const streamExplanation = async (params, onChunk, { signal } = {}) => {
  const cached = getCachedExplain(params);
  if (cached) {
    try { onChunk?.(cached, /*isFinal*/true); } catch {}
    return cached;
  }
  const key = getByokKey();
  // Своя нейросеть из настроек → свой ключ OpenRouter → общий лимит сайта.
  const gen = isCustomAIReady()
    ? streamChat({ prompt: _buildPrompt(params), signal })
    : key
    ? _streamOpenRouterDirect({ key, prompt: _buildPrompt(params), signal })
    : _streamProxy({ params, signal });

  let acc = "";
  try {
    for await (const chunk of gen) {
      acc += chunk;
      try { onChunk?.(acc, false); } catch {}
    }
  } catch (e) {
    if (e.name === "AbortError") throw e;
    throw e;
  }
  if (acc.trim()) _saveCache(params, acc);
  try { onChunk?.(acc, true); } catch {}
  return acc;
};
